"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  ResultTable,
} from "@/components/tools/stats-descriptive/ui";
import {
  columnNames,
  DatasetInput,
  useDatasetInput,
} from "@/components/tools/stats-regression/dataset-input";
import { HeatApaTable } from "@/components/tools/stats-regression/matrix-tables";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  formatPValue,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  apaTable,
  matrixCsv,
  toTsv,
} from "@/lib/tool-kit/stats-regression/apa-table";
import {
  type Adjustment,
  type CorrelationMethod,
  correlationMatrix,
  type MatrixReport,
  type MissingMode,
  rankedPairs,
} from "@/lib/tool-kit/stats-regression/correlation-matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const CORRELATION_MATRIX_CALCULATOR_STRINGS = {
  dataLabel: "Data — one row per case, one column per variable",
  dataHint:
    "Paste from a spreadsheet (with a header row if you have one), or separate values with tabs, commas, semicolons or spaces. Blank cells and NA count as missing; text columns are skipped.",
  placeholder: "Study\tSleep\tGrade\n17\t8.4\t75\n10\t6.8\t62",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  readSummary: "Read {rows} × {columns}.",
  rowCount: "one: {count} row|other: {count} rows",
  columnCount: "one: {count} column|other: {count} columns",
  moreRows: "one: … and {count} more row|other: … and {count} more rows",
  missingCells:
    "one: {count} cell is blank or NA.|other: {count} cells are blank or NA.",
  variableName: "Variable {number}",
  example:
    "Study\tSleep\tStress\tCaffeine\tGrade\n17\t8.4\t5\t3\t75\n10\t6.8\t5\t0\t62\n13\t6.6\t6\t1\t72\n15\t4.9\t6\t3\t74\n14\t7.3\t7\t3\t79\n12\t6.4\t6\t2\t75\n18\t\t7\t2\t72\n12\t7.7\t5\t0\t83\n20\t7.2\t8\t5\t76\n12\t6.4\t5\t2\t71\n17\t7\t3\t3\t78\n21\t4.4\t9\t4\t73\n6\t6.8\t3\t0\t67\n11\t6.2\t5\t2\t70\n11\t4.7\t7\t2\t64\n15\t6.7\t8\t2\t73\n11\t7.3\t4\t2\t70\n2\t7.6\t4\t0\t60\n2\t8.8\t2\t0\t58\n17\t5.8\t8\t2\t71\n11\t5.7\t7\t2\t64\n5\t8.1\t4\t1\t80\n11\t6.3\t4\t0\t64\n17\t7.9\t6\t1\t79",
  methodLabel: "Correlation",
  methodPearson: "Pearson's r",
  methodSpearman: "Spearman's ρ (ranks)",
  missingLabel: "Missing values",
  missingPairwise: "Pairwise: use every case each pair has",
  missingListwise: "Listwise: only cases complete on all variables",
  adjustLabel: "Multiple-comparison adjustment",
  adjustNone: "None (raw p-values)",
  adjustHolm: "Holm",
  adjustBonferroni: "Bonferroni",
  decimalsLabel: "Decimal places",
  variablesLabel: "Variables to include",
  skippedColumns:
    "one: Skipped a column that is not numeric: {names}.|other: Skipped columns that are not numeric: {names}.",
  nameSeparator: ", ",
  needVariables:
    "Select at least two numeric variables. Paste a spreadsheet with a column for each variable.",
  results: "Results",
  variablesCard: "Variables",
  casesCard: "Cases (n)",
  pairsCard: "Pairs tested",
  significantCard: "Significant pairs (p < .05)",
  strongestCard: "Strongest pair",
  strongestValue: "{a} & {b}: {r}",
  casesRange: "{low}–{high}",
  droppedRows:
    "one: {count} row with a missing value was left out (listwise deletion).|other: {count} rows with missing values were left out (listwise deletion).",
  apaTitle: "Correlation table (APA style)",
  apaVariable: "Variable",
  apaMean: "M",
  apaSd: "SD",
  noteLine: "Note. {method}; {missing}. N = {n}. {stars}{adjusted}",
  noteStars: "* p < .05. ** p < .01. *** p < .001.",
  noteAdjusted:
    " p-values adjusted with the {adjust} method across {count} tests.",
  noteMethodPearson: "Pearson correlations",
  noteMethodSpearman: "Spearman rank correlations",
  noteMissingPairwise: "pairwise deletion",
  noteMissingListwise: "listwise deletion",
  copyTable: "Copy table",
  copied: "Copied!",
  downloadCsv: "Download matrix (CSV)",
  csvFile: "correlation-matrix.csv",
  pTitle: "p-values",
  pTitleAdjusted: "p-values ({adjust}-adjusted)",
  pNote:
    "Two-tailed tests that each correlation differs from zero, from t = r√((n − 2) ÷ (1 − r²)) on n − 2 degrees of freedom.",
  pNoteSpearman:
    " For Spearman's ρ this t approximation is the usual large-sample p-value; with fewer than about 10 cases it is rough.",
  nTitle: "Cases behind each correlation",
  rankedTitle: "Strongest correlations",
  colPair: "Pair",
  colR: "r",
  colP: "p",
  colN: "n",
  pairLabel: "{a} & {b}",
  rankedMore:
    "Showing the {shown} strongest of {total} pairs; the matrices above hold them all.",
  adjustNoticeNone:
    "Each p-value is unadjusted. With many pairs some will fall below .05 by chance alone: with {count} tests, expect about {expected} false alarms if no correlations were real. Consider the Holm adjustment.",
  undefinedNote:
    "A dash means the correlation is undefined: a variable is constant, or the pair has fewer than two cases in common.",
} as const;

type Strings = typeof CORRELATION_MATRIX_CALCULATOR_STRINGS;

const DECIMALS = ["2", "3", "4"] as const;
type DecimalKey = (typeof DECIMALS)[number];
const SHOWN_PAIRS = 10;

function caseSummary(
  report: MatrixReport,
  fmt: NumberFormatter,
  strings: Strings
): string {
  const values = report.n.flatMap((row, i) =>
    row.filter((_, j) => (i === j ? report.missing === "listwise" : j > i))
  );
  const all = values.length > 0 ? values : [report.n[0][0]];
  const low = Math.min(...all);
  const high = Math.max(...all);
  return low === high
    ? fmt(low)
    : t(strings.casesRange, { low: fmt(low), high: fmt(high) });
}

function adjustName(adjustment: Adjustment, strings: Strings): string {
  return adjustment === "holm" ? strings.adjustHolm : strings.adjustBonferroni;
}

function pText(fmt: NumberFormatter, p: number): string {
  return Number.isNaN(p) ? "—" : formatPValue(fmt, p);
}

function PValueTable({
  report,
  names,
  fmt,
  strings,
}: {
  report: MatrixReport;
  names: string[];
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const title =
    report.adjustment === "none"
      ? strings.pTitle
      : t(strings.pTitleAdjusted, {
          adjust: adjustName(report.adjustment, strings),
        });
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{title}</h3>
      <ResultTable
        head={["", ...names]}
        rows={names.map((name, i) => [
          name,
          ...names.map((_, j) =>
            i === j ? "—" : pText(fmt, report.pAdjusted[i][j])
          ),
        ])}
        wrapFirstColumn
      />
      <Explanation>
        {strings.pNote}
        {report.method === "spearman" ? strings.pNoteSpearman : ""}
      </Explanation>
    </div>
  );
}

function RankedPairs({
  report,
  names,
  fmt,
  strings,
}: {
  report: MatrixReport;
  names: string[];
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const pairs = rankedPairs(report);
  const shown = pairs.slice(0, SHOWN_PAIRS);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.rankedTitle}</h3>
      <ResultTable
        head={[strings.colPair, strings.colR, strings.colP, strings.colN]}
        rows={shown.map((pair) => [
          t(strings.pairLabel, { a: names[pair.i], b: names[pair.j] }),
          formatStat(fmt, pair.r),
          pText(fmt, pair.p),
          fmt(pair.n),
        ])}
        wrapFirstColumn
      />
      {pairs.length > shown.length ? (
        <Explanation>
          {t(strings.rankedMore, {
            shown: fmt(shown.length),
            total: fmt(pairs.length),
          })}
        </Explanation>
      ) : null}
    </div>
  );
}

function ResultsView({
  report,
  names,
  decimals,
  fmt,
  strings,
}: {
  report: MatrixReport;
  names: string[];
  decimals: number;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const table = apaTable(report, names, fmt, decimals, {
    variable: strings.apaVariable,
    mean: strings.apaMean,
    sd: strings.apaSd,
  });
  const pairCount = (report.k * (report.k - 1)) / 2;
  const best = rankedPairs(report)[0];
  const significant = rankedPairs(report).filter(
    (pair) => pair.p < 0.05
  ).length;
  const note = t(strings.noteLine, {
    method:
      report.method === "pearson"
        ? strings.noteMethodPearson
        : strings.noteMethodSpearman,
    missing:
      report.missing === "pairwise"
        ? strings.noteMissingPairwise
        : strings.noteMissingListwise,
    n: caseSummary(report, fmt, strings),
    stars: strings.noteStars,
    adjusted:
      report.adjustment === "none"
        ? ""
        : t(strings.noteAdjusted, {
            adjust: adjustName(report.adjustment, strings),
            count: fmt(pairCount),
          }),
  });
  const varies =
    report.missing === "pairwise" &&
    new Set(report.n.flatMap((row, i) => row.filter((_, j) => j > i))).size > 1;
  return (
    <section className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <div aria-live="polite" className="space-y-4">
        <StatGrid>
          <StatCard
            emphasis
            label={strings.variablesCard}
            value={fmt(report.k)}
          />
          <StatCard
            label={strings.casesCard}
            value={caseSummary(report, fmt, strings)}
          />
          <StatCard label={strings.pairsCard} value={fmt(pairCount)} />
          <StatCard label={strings.significantCard} value={fmt(significant)} />
          <StatCard
            label={strings.strongestCard}
            value={t(strings.strongestValue, {
              a: names[best.i],
              b: names[best.j],
              r: formatStat(fmt, best.r),
            })}
          />
        </StatGrid>
        {report.adjustment === "none" && pairCount >= 6 ? (
          <Explanation>
            {t(strings.adjustNoticeNone, {
              count: fmt(pairCount),
              expected: fmt(pairCount * 0.05, { maximumFractionDigits: 1 }),
            })}
          </Explanation>
        ) : null}
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.apaTitle}</h3>
        <HeatApaTable report={report} table={table} />
        <Explanation>{note}</Explanation>
        <Explanation>{strings.undefinedNote}</Explanation>
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyTable}
            small
            text={toTsv(table)}
          />
          <button
            className={BUTTON_SMALL}
            onClick={() =>
              downloadText(
                strings.csvFile,
                matrixCsv(report.r, names),
                "text/csv;charset=utf-8"
              )
            }
            type="button"
          >
            {strings.downloadCsv}
          </button>
        </div>
      </div>
      <PValueTable fmt={fmt} names={names} report={report} strings={strings} />
      {varies ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.nTitle}</h3>
          <ResultTable
            head={["", ...names]}
            rows={names.map((name, i) => [
              name,
              ...names.map((_, j) => fmt(report.n[i][j])),
            ])}
            wrapFirstColumn
          />
        </div>
      ) : null}
      <RankedPairs fmt={fmt} names={names} report={report} strings={strings} />
    </section>
  );
}

export default function CorrelationMatrixCalculatorWidget() {
  const strings = useToolUi(CORRELATION_MATRIX_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const input = useDatasetInput();
  const [method, setMethod] = useState<CorrelationMethod>("pearson");
  const [missing, setMissing] = useState<MissingMode>("pairwise");
  const [adjustment, setAdjustment] = useState<Adjustment>("none");
  const [decimals, setDecimals] = useState<DecimalKey>("2");
  const [excluded, setExcluded] = useState<number[]>([]);

  const { dataset } = input;
  const allNames = columnNames(dataset, strings.variableName);
  const numeric = dataset.columns.flatMap((column, i) =>
    column.isNumeric ? [i] : []
  );
  const skipped = dataset.columns.flatMap((column, i) =>
    column.isNumeric ? [] : [allNames[i]]
  );
  const included = numeric.filter((i) => !excluded.includes(i));
  const report =
    included.length >= 2
      ? correlationMatrix(
          included.map((i) => dataset.columns[i].numbers),
          { method, missing, adjustment }
        )
      : null;

  const toggle = (index: number) =>
    setExcluded((list) =>
      list.includes(index) ? list.filter((i) => i !== index) : [...list, index]
    );

  return (
    <>
      <DatasetInput
        example={strings.example}
        fmt={fmt}
        id="cm-data"
        labels={strings}
        locale={locale}
        state={input}
      />
      {skipped.length > 0 ? (
        <Notice>
          {tPlural(strings.skippedColumns, locale, skipped.length, {
            names: skipped.join(strings.nameSeparator),
          }).trim()}
        </Notice>
      ) : null}

      {numeric.length >= 2 ? (
        <section className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <PillGroup
              label={strings.methodLabel}
              name="cm-method"
              onChange={setMethod}
              options={[
                { value: "pearson", label: strings.methodPearson },
                { value: "spearman", label: strings.methodSpearman },
              ]}
              value={method}
            />
            <PillGroup
              label={strings.missingLabel}
              name="cm-missing"
              onChange={setMissing}
              options={[
                { value: "pairwise", label: strings.missingPairwise },
                { value: "listwise", label: strings.missingListwise },
              ]}
              value={missing}
            />
            <SelectField
              id="cm-adjust"
              label={strings.adjustLabel}
              onChange={setAdjustment}
              options={[
                { value: "none", label: strings.adjustNone },
                { value: "holm", label: strings.adjustHolm },
                { value: "bonferroni", label: strings.adjustBonferroni },
              ]}
              value={adjustment}
            />
            <SelectField
              id="cm-decimals"
              label={strings.decimalsLabel}
              onChange={setDecimals}
              options={DECIMALS.map((value) => ({ value, label: value }))}
              value={decimals}
            />
          </div>
          <fieldset className="space-y-2">
            <legend className={LABEL}>{strings.variablesLabel}</legend>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {numeric.map((i) => (
                <Checkbox
                  checked={!excluded.includes(i)}
                  key={i}
                  label={allNames[i]}
                  onChange={() => toggle(i)}
                />
              ))}
            </div>
          </fieldset>
        </section>
      ) : null}

      {report ? (
        <ResultsView
          decimals={Number(decimals)}
          fmt={fmt}
          names={included.map((i) => allNames[i])}
          report={report}
          strings={strings}
        />
      ) : null}
      {report || dataset.columns.length === 0 ? null : (
        <Notice>{strings.needVariables}</Notice>
      )}
    </>
  );
}
